import type { Session, User } from '@supabase/supabase-js';
import { canonicalOrigin } from '../platform/index.js';
import { supabaseBrowser } from './supabaseClient.js';

/**
 * Browser auth state — the cached current Supabase session + user.
 * Backed by Supabase's own persistSession storage, surfaced here as
 * a synchronous getter + listener API so scenes don't need to await
 * the SDK on every render pass.
 *
 * Lifecycle:
 *
 *   1. main.ts calls initAuth() ONCE at boot. That awaits getSession()
 *      so initial-render scenes can call getAuth() synchronously.
 *
 *   2. supabase.auth.onAuthStateChange() drives subsequent updates —
 *      sign in, sign out, token refresh, password reset deep links —
 *      all funnel through here and notify listeners.
 *
 *   3. Scenes (HomeScene, etc.) call onAuthChange(...) to subscribe
 *      for re-renders, and read getAuth() for current state.
 *
 * Slice 1.2a scope: this module covers sign-up / sign-in / sign-out
 * only. Profile-row creation lands in 1.2b alongside UsernameSetupScene;
 * password reset lands in 1.2c.
 */

export interface AuthState {
  readonly user: User | null;
  readonly session: Session | null;
}

const EMPTY_STATE: AuthState = { user: null, session: null };

let cachedState: AuthState = EMPTY_STATE;
let initialized = false;
const listeners = new Set<(state: AuthState) => void>();

function setState(next: AuthState): void {
  cachedState = next;
  for (const fn of listeners) {
    try {
      fn(cachedState);
    } catch (e) {
      // Isolate buggy listeners — one throwing subscriber shouldn't
      // prevent the others from getting their update.
      console.error('auth listener threw', e);
    }
  }
}

/**
 * Load the persisted Supabase session into the cached state. Call
 * exactly once at app boot from main.ts. Subsequent state changes
 * arrive automatically via onAuthStateChange().
 *
 * Returns the AuthState so the caller can branch synchronously
 * after the await (e.g., route to AuthScene if no session and the
 * URL needs auth).
 */
export async function initAuth(): Promise<AuthState> {
  if (initialized) return cachedState;
  initialized = true;

  const supabase = supabaseBrowser();

  // Pull the persisted session synchronously from localStorage via
  // the SDK. This is async because some storage backends are async
  // (IndexedDB, native shells); for web/localStorage it resolves
  // essentially instantly.
  const { data } = await supabase.auth.getSession();
  cachedState = {
    user: data.session?.user ?? null,
    session: data.session,
  };

  // Propagate the JWT to the Realtime client. Without this, the
  // websocket connects as `anon` and every postgres_changes event
  // on an RLS-protected table (games, game_seats) gets silently
  // suppressed because anon can't satisfy the SELECT policies. The
  // SDK is supposed to auto-attach the token from the auth state,
  // but there's a long-standing race where a channel subscribing
  // before this binding completes stays on the anon channel for
  // its full lifetime — symptom: refresh shows fresh state, but
  // live events never arrive. setAuth() forces the binding before
  // any scene gets a chance to subscribe.
  if (data.session) {
    supabase.realtime.setAuth(data.session.access_token);
  }

  // Subsequent transitions (sign-in, sign-out, token refresh, magic
  // link consumption) all fire here. We mirror the SDK's session
  // into our cache + notify listeners. We ALSO call realtime.setAuth
  // on every transition so an in-progress channel survives a token
  // refresh (Supabase rotates the JWT every ~hour by default; a
  // game lobby left open across the rotation would otherwise go
  // silent the moment the token expired).
  supabase.auth.onAuthStateChange((_event, session) => {
    if (session) {
      supabase.realtime.setAuth(session.access_token);
    } else {
      // Sign-out: re-binding to the anon publishable key clears the
      // user-JWT context so the next anon-context channel (if any)
      // doesn't try to use a dead token. We can't pass null to
      // setAuth in @supabase/supabase-js v2.x — the typed signature
      // is string-only — but calling it with an empty string is the
      // documented "go back to anon" pattern.
      supabase.realtime.setAuth('');
    }
    setState({
      user: session?.user ?? null,
      session,
    });
  });

  return cachedState;
}

export function getAuth(): AuthState {
  return cachedState;
}

export function onAuthChange(fn: (state: AuthState) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export type AuthResult = { ok: true } | { ok: false; error: string };

/**
 * Sign up with email + password. The Supabase project must have
 * email confirmation enabled (per SUPABASE_SETUP.md §2) — on success,
 * a confirmation email is sent and the user is NOT signed in until
 * they click the link.
 *
 * emailRedirectTo: hands Supabase the exact URL we want the email
 * link to redirect back to. Without this, Supabase falls back to
 * the project's Site URL — which is fine in production but breaks
 * on preview deploys (the link redirects to prod, the session lands
 * at the wrong origin's localStorage, and the preview URL still
 * looks logged-out).
 *
 * The optional `returnTo` path lets callers preserve deep-link
 * intent across the email-confirmation round trip — e.g., a user
 * signing up from `/game/abc` lands back at `/game/abc` after
 * confirming, not at the root. The path is appended to
 * canonicalOrigin() and the resulting URL still needs to be in
 * Supabase's Redirect URLs allowlist (a wildcard like
 * `https://battleshark.online/**` covers all sub-paths).
 */
export async function signUp(
  email: string,
  password: string,
  options?: { returnTo?: string }
): Promise<AuthResult> {
  const supabase = supabaseBrowser();
  const emailRedirectTo = options?.returnTo
    ? `${canonicalOrigin()}${options.returnTo}`
    : canonicalOrigin();
  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo },
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function signIn(
  email: string,
  password: string
): Promise<AuthResult> {
  const supabase = supabaseBrowser();
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function signOut(): Promise<void> {
  const supabase = supabaseBrowser();
  await supabase.auth.signOut();
}

/**
 * Re-send the email-confirmation link. Triggered from the
 * "Confirm your email" banner on HomeScene when the user reports
 * they didn't receive the original (typo in email, spam folder
 * already cleared, etc.). Idempotent on Supabase's side — rate
 * limits apply but Supabase enforces them server-side.
 *
 * Same `returnTo` semantics as signUp() — preserves deep-link
 * intent across the resend.
 */
export async function resendConfirmation(
  email: string,
  options?: { returnTo?: string }
): Promise<AuthResult> {
  const supabase = supabaseBrowser();
  const emailRedirectTo = options?.returnTo
    ? `${canonicalOrigin()}${options.returnTo}`
    : canonicalOrigin();
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo },
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * Send the password-reset email. The redirectTo URL is where Supabase
 * sends the user after they click the link — and is what tells our
 * client-side router to mount ResetPasswordScene. Must match a pattern
 * in Supabase's Redirect URLs allowlist (SUPABASE_SETUP.md §3).
 *
 * Calling this for an email that isn't registered does NOT fail —
 * Supabase deliberately returns success either way to prevent email-
 * enumeration attacks (an attacker can't probe "is this email in the
 * system" by trying to reset). The UX consequence: we always show
 * the "check your email" success state, even on a typo. Acceptable
 * trade-off for the security win.
 */
export async function requestPasswordReset(
  email: string
): Promise<AuthResult> {
  const supabase = supabaseBrowser();
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${canonicalOrigin()}/reset-password`,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * Set a new password for the currently signed-in user. Called from
 * ResetPasswordScene after the user clicks the magic link in the
 * reset email — at that point Supabase has signed them in with a
 * recovery-mode session and updateUser is the only thing that
 * session is permitted to do.
 *
 * Same minimum length we enforce in AuthScene (8 chars). Real check
 * happens server-side; this is UX.
 */
export async function updatePassword(
  newPassword: string
): Promise<AuthResult> {
  const supabase = supabaseBrowser();
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * Initiates an email change. Calls `supabase.auth.updateUser({ email })`
 * which queues a "Confirm new email" link to the NEW address and (after
 * the user clicks it) a "Your email was changed" notification to the
 * OLD address — both use the already-branded Supabase auth templates.
 *
 * The auth.users row's email doesn't actually flip until the
 * confirmation link is clicked; callers should surface a "Check your
 * inbox" message after a successful call.
 */
export async function updateEmail(newEmail: string): Promise<AuthResult> {
  const supabase = supabaseBrowser();
  const { error } = await supabase.auth.updateUser({ email: newEmail });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
